<script lang="ts">
import { RouteNames } from "@/utils/constants.ts";

export function getRouteOnboardPersonalQuestionnaire() {
  return {
    name: RouteNames.ONBOARDING_PERSON_QUESTIONNAIRE
  };
}
</script>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import CardLayout from "@/components/layout/CardLayout.vue";
import { TextInput } from "@/components/form/index.ts";
import InputSelect from "@/components/form/InputSelect.vue";
import BasicFormTitle from "@/components/basic/BasicFormTitle.vue";
import { getRouteOnboardPersonalProfile } from "@/views/Onboarding/OnboardingPersonalProfile.vue";
import { getRouteOnboardPersonalVerification } from "@/views/Onboarding/OnboardingPersonalVerification.vue";
import { PersonService } from "@/services";
import { error } from "@/utils/toast.ts";
import Countries from "@/libs/core/countries.ts";
import { usePersonStore } from "@/store";

const router = useRouter();
const personService = new PersonService();
const personStore = usePersonStore();
const isLoading = ref(false);

// Prepare countries list
const countries: any = {};
for (const i in Countries) {
  const country = Countries[i];
  countries[country.code3] = country;
}

// Employment Status (checkboxes, not radio)
const employedChecked = ref(false);
const unemployedChecked = ref(false);
const studentChecked = ref(false);
const selfEmployedChecked = ref(false);
const currentPosition = ref("");
const employerName = ref("");
const activityArea = ref("");

// Purpose of Account
const purposeDepositSavings = ref(false);
const purposeSalaryIncome = ref(false);
const purposeHouseholdExpenses = ref(false);
const purposeLoan = ref(false);
const purposeBusiness = ref(false);
const purposeOther = ref(false);
const purposeOtherText = ref("");

// Source of incoming funds
const sourceSalary = ref(false);
const sourceSavings = ref(false);
const sourceDividends = ref(false);
const sourceInvestmentActivity = ref(false);
const sourceLoans = ref(false);
const sourceLoansText = ref("");
const sourceInheritance = ref(false);
const sourceInheritanceText = ref("");
const sourceGiftsDonations = ref(false);
const sourceGiftsDonationsText = ref("");
const sourceOther = ref(false);
const sourceOtherText = ref("");

// Monthly turnover options
const turnoverOptions = {
  "up_to_2000": "Up to 2,000 EUR",
  "2000_to_5000": "2,000 to 5,000 EUR",
  "5000_to_10000": "5,000 to 10,000 EUR",
  "10000_to_25000": "10,000 to 25,000 EUR",
  "25000_to_50000": "25,000 to 50,000 EUR",
  "over_50000": "Over 50,000 EUR",
  "other": "Other"
};
const monthlyTurnover = ref("up_to_2000");

// Average transaction value options
const transactionValueOptions = {
  "up_to_1000": "Up to 1,000 EUR",
  "1000_to_5000": "1,000 to 5,000 EUR",
  "5000_to_10000": "5,000 to 10,000 EUR",
  "over_10000": "Over 10,000 EUR",
  "other": "Other"
};
const averageTransactionValue = ref("up_to_1000");

// Incoming transactions options
const incomingTransactionsOptions = {
  "up_to_10": "Up to 10",
  "10_to_20": "10 to 20",
  "20_to_50": "20 to 50",
  "over_50": "Over 50",
  "other": "Other"
};
const incomingTransactionsPerMonth = ref("up_to_10");

// Outgoing transactions options
const outgoingTransactionsOptions = {
  "up_to_10": "Up to 10",
  "10_to_20": "10 to 20",
  "20_to_50": "20 to 50",
  "over_50": "Over 50",
  "other": "Other"
};
const outgoingTransactionsPerMonth = ref("other");

// Countries to send funds
const sendToEuEea = ref(true);
const sendToNonEuEea = ref(false);
const selectedCountries = ref<string>('');

async function submitForm() {
  isLoading.value = true;
  try {
    const questionnaireData = {
      employmentStatus: {
        employed: employedChecked.value,
        unemployed: unemployedChecked.value,
        student: studentChecked.value,
        selfEmployed: selfEmployedChecked.value
      },
      currentPosition: currentPosition.value,
      employerName: employerName.value,
      activityArea: activityArea.value,
      purposeOfAccount: {
        depositSavings: purposeDepositSavings.value,
        salaryIncome: purposeSalaryIncome.value,
        householdExpenses: purposeHouseholdExpenses.value,
        loan: purposeLoan.value,
        business: purposeBusiness.value,
        other: purposeOther.value,
        otherText: purposeOtherText.value
      },
      sourceOfFunds: {
        salary: sourceSalary.value,
        savings: sourceSavings.value,
        dividends: sourceDividends.value,
        investmentActivity: sourceInvestmentActivity.value,
        loans: sourceLoans.value,
        loansText: sourceLoansText.value,
        inheritance: sourceInheritance.value,
        inheritanceText: sourceInheritanceText.value,
        giftsDonations: sourceGiftsDonations.value,
        giftsDonationsText: sourceGiftsDonationsText.value,
        other: sourceOther.value,
        otherText: sourceOtherText.value
      },
      monthlyTurnover: monthlyTurnover.value,
      averageTransactionValue: averageTransactionValue.value,
      incomingTransactionsPerMonth: incomingTransactionsPerMonth.value,
      outgoingTransactionsPerMonth: outgoingTransactionsPerMonth.value,
      sendToEuEea: sendToEuEea.value,
      sendToNonEuEea: sendToNonEuEea.value,
      selectedCountries: selectedCountries.value
    };

    console.log("Questionnaire data:", questionnaireData);

    // Save questionnaire data to backend
    await personService.createPersonalQuestionnaire("personal-questionnaire", questionnaireData);

    // Refresh person state to get updated questionnaire data
    await personStore.getState();

    // Navigate to verification page
    await router.push(getRouteOnboardPersonalVerification());
  } catch (e) {
    console.error("Error submitting questionnaire:", e);
    error("Failed to save questionnaire");
  } finally {
    isLoading.value = false;
  }
}

const goBack = () => {
  router.push(getRouteOnboardPersonalProfile());
};

const isFormValid = computed(() => {
  // Employment validation
  if (employedChecked.value && (!currentPosition.value || !employerName.value)) {
    return false;
  }
  if (selfEmployedChecked.value && !activityArea.value) {
    return false;
  }

  // At least one employment status must be selected
  const hasEmploymentStatus = employedChecked.value || unemployedChecked.value ||
                               studentChecked.value || selfEmployedChecked.value;

  // At least one purpose must be selected
  const hasPurpose = purposeDepositSavings.value || purposeSalaryIncome.value ||
                     purposeHouseholdExpenses.value || purposeLoan.value ||
                     purposeBusiness.value || purposeOther.value;

  // At least one source must be selected
  const hasSource = sourceSalary.value || sourceSavings.value || sourceDividends.value ||
                    sourceInvestmentActivity.value || sourceLoans.value ||
                    sourceInheritance.value || sourceGiftsDonations.value || sourceOther.value;

  return hasEmploymentStatus && hasPurpose && hasSource;
});
</script>

<template>
  <CardLayout
    title="Personal info"
    subtitle="Application for opening and maintaining a personal current account"
    heading="Questionnaire"
    :max-steps="4"
    :current-step="2"
    :next="submitForm"
    :back="goBack"
    :isAllowedNext="isFormValid"
  >
    <div class="flex flex-col w-full gap-6 md:gap-8">
      <!-- Employment Status -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Employment Status
        </BasicFormTitle>
      </div>

      <div class="flex flex-col gap-4 px-4 md:px-0">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="employedChecked"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Employed</span>
        </label>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">What is your current position?*</label>
          <TextInput
            type="text"
            v-model="currentPosition"
            placeholder="Enter"
          />
        </div>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">What is your current employer's name?*</label>
          <TextInput
            type="text"
            v-model="employerName"
            placeholder="Enter"
          />
        </div>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="unemployedChecked"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Unemployed</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="studentChecked"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Student</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="selfEmployedChecked"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Self-employed</span>
        </label>

        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Please specify the area of activity*</label>
          <TextInput
            type="text"
            v-model="activityArea"
            placeholder="Enter"
          />
        </div>
      </div>

      <!-- Purpose of the Account -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Purpose of the Account
        </BasicFormTitle>
      </div>

      <div class="flex flex-col gap-3 px-4 md:px-0">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="purposeDepositSavings"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Deposit/ Savings</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="purposeSalaryIncome"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Receipt of monthly salary/ Income</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="purposeHouseholdExpenses"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Household expenses</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="purposeLoan"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Loan</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="purposeBusiness"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Business</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="purposeOther"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Other:</span>
        </label>

        <div v-if="purposeOther">
          <TextInput
            type="text"
            v-model="purposeOtherText"
            placeholder="Other"
          />
        </div>
      </div>

      <!-- Source of incoming funds -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          What will be the source of incoming funds to your account?
        </BasicFormTitle>
      </div>

      <div class="flex flex-col gap-3 px-4 md:px-0">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceSalary"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Salary</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceSavings"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Savings</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceDividends"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Dividends</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceInvestmentActivity"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Investment activity</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceLoans"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Loans:</span>
        </label>

        <TextInput
          type="text"
          v-model="sourceLoansText"
          placeholder="Loans"
        />

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceOther"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Other:</span>
        </label>

        <TextInput
          type="text"
          v-model="sourceOtherText"
          placeholder="Other"
        />

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceSalary"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Salary</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceSavings"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Savings</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceInvestmentActivity"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Investment activities</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceDividends"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Dividends</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceInheritance"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Inheritance:</span>
        </label>

        <TextInput
          type="text"
          v-model="sourceInheritanceText"
          placeholder="inheritance"
        />

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceGiftsDonations"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Gifts/Donations:</span>
        </label>

        <TextInput
          type="text"
          v-model="sourceGiftsDonationsText"
          placeholder="Gifts/ donations"
        />

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sourceOther"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Other:</span>
        </label>

        <TextInput
          type="text"
          v-model="sourceOtherText"
          placeholder="Other"
        />
      </div>

      <!-- Monthly turnover -->
      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Please specify your planned monthly turnover *</label>
          <InputSelect
            :list="turnoverOptions"
            v-model="monthlyTurnover"
          >
            <template #selected="{ item }">
              <span>{{ item }}</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Average transaction value -->
      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Please specify the planned value of average one-time transaction *</label>
          <InputSelect
            :list="transactionValueOptions"
            v-model="averageTransactionValue"
          >
            <template #selected="{ item }">
              <span>{{ item }}</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Incoming transactions per month -->
      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Please specify the planned amount of incoming transactions per month *</label>
          <InputSelect
            :list="incomingTransactionsOptions"
            v-model="incomingTransactionsPerMonth"
          >
            <template #selected="{ item }">
              <span>{{ item }}</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Outgoing transactions per month -->
      <div class="px-4 md:px-0">
        <div class="flex flex-col gap-y-2">
          <label class="text-sm font-medium text-gray-700">Please specify the planned amount of outgoing transactions per month *</label>
          <InputSelect
            :list="outgoingTransactionsOptions"
            v-model="outgoingTransactionsPerMonth"
          >
            <template #selected="{ item }">
              <span>{{ item }}</span>
            </template>
            <template #item="{ item }">
              <span>{{ item }}</span>
            </template>
          </InputSelect>
        </div>
      </div>

      <!-- Countries to send funds -->
      <div class="px-4 md:px-0">
        <BasicFormTitle>
          Please specify all countries to which you expect to send funds
        </BasicFormTitle>
      </div>

      <div class="flex flex-col gap-3 px-4 md:px-0">
        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sendToEuEea"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">EU/ EEA counterparties</span>
        </label>

        <label class="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            v-model="sendToNonEuEea"
            class="w-4 h-4 text-blue-500 border-gray-300 rounded focus:ring-blue-500"
          />
          <span class="text-sm text-gray-700">Follow non-EU/ EEA counterparties, please list the countries below</span>
        </label>

        <div v-if="sendToNonEuEea" class="ml-6">
          <div class="flex flex-col gap-y-2">
            <label class="text-sm font-medium text-gray-700">If other than EU/ EEA counterparties, please list the countries</label>
            <InputSelect
              :list="countries"
              v-model="selectedCountries"
            >
              <template #selected="{ item }">
                <span v-if="item">{{ item.name }}</span>
                <span v-else class="text-gray-400">Select countries</span>
              </template>
              <template #item="{ item }">
                <span>{{ item.name }}</span>
              </template>
            </InputSelect>
          </div>
        </div>
      </div>

      <div v-if="isLoading" class="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-400 px-4 md:px-0">
        <div class="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
        <span class="text-sm">Submitting...</span>
      </div>
    </div>
  </CardLayout>
</template>

<style scoped lang="scss">
</style>
